class OfflineError extends Error {
    constructor() {
        super('');
        this.name = "OfflineError";
    }
}

class AS400Error extends Error {
    constructor(message = 'AS/400 system error', errorType = 'UNKNOWN') {
        super(message);
        this.name = 'AS400Error';
        this.errorType = errorType;
    }
}

class AS400TimeoutError extends AS400Error {
    constructor(message = 'AS/400 request timed out') {
        super(message, 'TIMEOUT');
        this.name = 'AS400TimeoutError';
    }
}

class AS400ConnectionError extends AS400Error {
    constructor(message = 'AS/400 connection failed') {
        super(message, 'CONNECTION');
        this.name = 'AS400ConnectionError';
    }
}

class ApiResponseError extends Error {
    constructor(message, { status, payload } = {}) {
        super(message);
        this.name = 'ApiResponseError';
        this.status = status;
        this.payload = payload;
        this.isApiError = true;
    }
}


class CartCheckout extends HTMLElement {

    constructor() {
        super();

        this._loading = false;
        this.confirmationPage = this.dataset.confirmationPage;
        this.defaultShippingCosts = parseInt(this.dataset.shippingCosts);
        this.shippingFreeThreshold = parseInt(this.dataset.shippingFreeThreshold);
        // Configurable timeout options (in minutes)
        this.checkout_timeout = parseInt(this.dataset.checkoutTimeout) || 15;
        this.api_timeout = 150; // 150 seconds for API calls
        this.retry_timeout = parseInt(this.dataset.retryTimeout) || 10; // 10 seconds max delay for retries
        this.token = this.dataset.token;
        this.checkoutId = localStorage.getItem('checkoutId');
        this.deliveryDate = this.dataset.date ? new Date(this.dataset.date) : new Date();
        this.empty = this.dataset.empty == 'true';
        this.isCartPage = window.location.pathname.includes(`/cart`);
        
        // Track active abort controllers for cleanup
        this.activeControllers = new Set();

        /**
         * Main Buttons
         */
        this.submitCart = this.querySelector('[data-send]');
        this.submitApprove = this.querySelector('[data-submit-approve]');

        /**
         * ERROR Modal
         */
        this.errorModal = document.getElementById('checkout-response-error');
        this.checkoutResponseErrorModal = this.errorModal; // Alias for legacy code
        this.errorModalReset = this.errorModal?.querySelector('[data-checkout-error-reset]');
        this.errorModalRestore = this.errorModal?.querySelector('[data-checkout-cart-restore]');

        /**
         * OFFLINE STOCK Modal
         */
        this.offlineStockModal = document.getElementById('inventory-offline');
        this.offlineStockAccept = this.offlineStockModal?.querySelector('[data-accept-offline]');
        this.offlineStockCancel = this.offlineStockModal?.querySelector('[data-cancel-offline]');

        // Store bound method references
        this._boundMethods = {
            handleCreate: this.handleCreate.bind(this),
            handleApprove: this.handleApprove.bind(this),
            handleTermsCheck: this.handleTermsCheck.bind(this),
            handleOfflineStockAccept: this.handleOfflineStockAccept.bind(this),
            handleOfflineStockCancel: this.handleOfflineStockCancel.bind(this),
            handleErrorModalReset: this.handleErrorModalReset.bind(this),
            handleErrorModalRestore: this.handleErrorModalRestore.bind(this),
        };

         // Cache for DOM elements
        this._domCache = {
            cartItems: document.querySelector('cart-items'),
            deliveryDateModalOpener: document.querySelector('.cart__header-container modal-opener[data-modal="select-date"] button'),
        };
    }

    /**
     * Controls loading state and UI updates
     * @param {boolean} value - True to enable loading, false to disable
     */
    set loading(value) {

        // Prevent redundant updates
        if (this._loading === value) return;
        
        this._loading = value;
        
        // Use cached DOM references
        const cartItems = this._domCache.cartItems;
        const dateModalOpener = this._domCache.deliveryDateModalOpener;
        
        // Dispatch loading events
        document.dispatchEvent(new CustomEvent(value ? 'request:loading:start' : 'request:loading:end'));
        
        // Update cart items interaction
        if (cartItems) {
            cartItems.classList.toggle('pointer-events-none', value);
        }
        
        if (value) {
            // Loading started
            document.activeElement.blur();
            
            // Disable and show loading on date modal opener
            if (dateModalOpener) {
                dateModalOpener.disabled = true;
                dateModalOpener.classList.add('is-loading');
            }
            
            // Disable and show loading on submit button
            if (this.submitCart) {
                this.submitCart.classList.add('is-loading');
                this.submitCart.disabled = true;
            }
        } else {
            // Loading finished
            
            // Re-enable date modal opener
            if (dateModalOpener) {
                dateModalOpener.disabled = false;
                dateModalOpener.classList.remove('is-loading');
            }
            
            // Re-enable submit button
            if (this.submitCart) {
                this.submitCart.classList.remove('is-loading');
                
                // Use requestAnimationFrame instead of setTimeout
                // This waits for the next browser paint cycle (safer than setTimeout)
                requestAnimationFrame(() => {
                    // Only enable if all terms are checked
                    const checkedTerms = document.querySelectorAll('.js-cart-terms:checked');
                    const totalTerms = document.querySelectorAll('.js-cart-terms');
                    const allChecked = checkedTerms.length === totalTerms.length;
                    if (allChecked) {
                        this.submitCart.disabled = false;
                    }
                });
            }
        }
    }

    get loading() {
        return this._loading;
    }

    /**
     * 
     */
    connectedCallback() {
        console.log('🚀 CartCheckout component connected');

        //Init
        this._init();

        // Terms Checkboxes - Always attach these even if cart is empty
        document.querySelectorAll('.js-cart-terms')?.forEach((checkbox) => checkbox.addEventListener('click', this._boundMethods.handleTermsCheck));

        if (this.empty) {
            return;
        }

        /**
         * Used stored bound methods for all event listeners
         */

        //Main Button Actions
        this.submitCart?.addEventListener('click', this._boundMethods.handleCreate);
        this.submitApprove?.addEventListener('click', this._boundMethods.handleApprove);
        
        //OFFLINE STOCK Buttons
        this.offlineStockAccept?.addEventListener('click', this._boundMethods.handleOfflineStockAccept);
        this.offlineStockCancel?.addEventListener('click', this._boundMethods.handleOfflineStockCancel);

        //ERROR Buttons
        this.errorModalReset?.addEventListener('click', this._boundMethods.handleErrorModalReset);
        this.errorModalRestore?.addEventListener('click', this._boundMethods.handleErrorModalRestore);
    }

    disconnectedCallback() {
        console.log('🚪 CartCheckout component disconnected');

        // Cleanup any active requests
        this._cleanupActiveRequests();

         // Clear DOM cache
        this._domCache.cartItems = null;
        this._domCache.deliveryDateModalOpener = null;

        //Terms Checkboxes
        document.querySelectorAll('.js-cart-terms')?.forEach((checkbox) => checkbox.removeEventListener('click', this._boundMethods.handleTermsCheck));

        if (this.empty) {
            return;
        }

        /**
         * Used stored bound methods for all event listeners
         */

        //Main Button Actions
        this.submitCart?.removeEventListener('click', this._boundMethods.handleCreate);
        this.submitApprove?.removeEventListener('click', this._boundMethods.handleApprove);
        
        //OFFLINE STOCK Buttons
        this.offlineStockAccept?.removeEventListener('click', this._boundMethods.handleOfflineStockAccept);
        this.offlineStockCancel?.removeEventListener('click', this._boundMethods.handleOfflineStockCancel);

        //ERROR Buttons
        this.errorModalReset?.removeEventListener('click', this._boundMethods.handleErrorModalReset);
        this.errorModalRestore?.removeEventListener('click', this._boundMethods.handleErrorModalRestore);
    }

    /**
     * 
     */

    _init() {

        window.checkout = this;
        //console.log('📝 submitCart button:', this.submitCart);
        this._validateTerms();

        if (localStorage.getItem('checkout_progress') == null) {
            document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 1 }));
        } else if (document.querySelectorAll('tr[data-has-errors="true"]').length > 0) {
            document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 2 }));
        }

        if (!this.empty && !window.customer?.currentUserNumber) {
            if (this.checkoutResponseErrorModal) {
                this.checkoutResponseErrorModal.html = '';
                this.checkoutResponseErrorModal.message = `CustomerNumber missing!`;
                this.checkoutResponseErrorModal.stack = '';
                //setTimeout(() => this.checkoutResponseErrorModal.open(this), 100);
            }
            return;
        }

        this._handleShippingCosts();
    }

    /**
     * TERMS functions
     */

    _validateTerms() {
        // Simple approach: Check each required checkbox individually
        let allRequiredChecked = true;
        let debugInfo = [];

        // Terms 1 is always required
        const terms1 = document.querySelector('#cart-terms-1');
        if (terms1) {
            debugInfo.push(`Terms 1: ${terms1.checked ? 'checked' : 'unchecked'}`);
            if (!terms1.checked) {
                allRequiredChecked = false;
            }
        } else {
            debugInfo.push('Terms 1: not found');
        }

        // Terms 2 is required only if it exists (GTC page configured)
        const terms2 = document.querySelector('#cart-terms-2');
        if (terms2) {
            debugInfo.push(`Terms 2: ${terms2.checked ? 'checked' : 'unchecked'}`);
            if (!terms2.checked) {
                allRequiredChecked = false;
            }
        } else {
            debugInfo.push('Terms 2: not found (optional)');
        }

        // Terms 3 is required only if it exists (all three pages configured)
        const terms3 = document.querySelector('#cart-terms-3');
        if (terms3) {
            debugInfo.push(`Terms 3: ${terms3.checked ? 'checked' : 'unchecked'}`);
            if (!terms3.checked) {
                allRequiredChecked = false;
            }
        } else {
            debugInfo.push('Terms 3: not found (optional)');
        }

        console.log('🔍 Terms validation:', debugInfo.join(', '), `→ Button ${allRequiredChecked ? 'enabled' : 'disabled'}`);

        if (this.submitCart) {
            this.submitCart.disabled = !allRequiredChecked;
            this.submitCart.setAttribute('aria-disabled', `${!allRequiredChecked}`);
            //console.log('✅ Submit button updated:', this.submitCart.disabled ? 'disabled' : 'enabled');
        } else {
            console.log('❌ Submit button not found');
        }

        return allRequiredChecked;
    }

    async handleTermsCheck({ target }) {
        // Back to simple working logic
        this.termsCheckboxes = Array.from(document.querySelectorAll('.js-cart-terms'));
        const allChecked = document.querySelectorAll('.js-cart-terms:checked').length == this.termsCheckboxes.length;

        // enabled once when ALL become checked
        if (!allChecked){
            this.submitCart.disabled = !allChecked;
            this.submitCart.setAttribute('aria-disabled', `${!allChecked}`);
            return;
        }

        this.submitCart.disabled = !allChecked;
        this.submitCart.setAttribute('aria-disabled', `${!allChecked}`);
    }

    /**
     * CREATE CHECKOUT functions
     */
    async handleCreate() {
        console.log('🔒 CHECKOUT STARTING');
        localStorage.setItem('checkout_protection_active', 'true');
        localStorage.setItem('checkout_started', 'true');
        localStorage.setItem('checkout_lock_timestamp', Date.now().toString());
        document.body.dataset.checkoutActive = 'true';
        document.body.dataset.cartLocked = 'true';

        this.loading = true;
        
        try {
            // Clear any previous error messages
            this._clearCartErrorMessage();

            //enable loader
            this.submitCart.classList.add('is-loading');
            document.body.classList.add('checkout-loading');

            // Set Lock attr when checkout starts
            console.log('🔒 Locking checkout...');
            
            const formData = new FormData();
            formData.set('attributes[locked]', 'true');
            await this._updateCartAtributes(formData);

            LocalCart.updateCheckoutState(true);

            //Clear old properties of items
            console.log('⏳ Clear Items Properties If Old Checkout Response Properties Are There...');
            await this._clearItemProperties();

            // if(sharedCart && Object.keys(sharedCart).length){
            //     await sharedCart.handleCartChange();
            // }

            document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 2 }));

            const timeout = new Date(new Date().getTime() + this.checkout_timeout * 60000).getTime();
            const update_checkout_before = timeout - (15 * 1000);

            localStorage.setItem('checkout_progress', 'true');
            localStorage.setItem('checkout_timeout', timeout);
            localStorage.setItem('checkout_update_checkout_timeout', update_checkout_before);

            // First call start_checkout to get AS/400 stock updates
            await this._create();
            
        } catch (error) {
            // NEW: Single unified error handler
            await this._handleCheckoutError(error, 'handleCreate');
        }
    }

    async _create() {

        window.scrollTo({ top: 0, behavior: 'smooth' });

        const { checkoutId, checkout, new_user_identifier, as400_response, auto_completed, message } = await this._sendStartCheckout();
        const { data } = checkout;

        if (new_user_identifier != null && new_user_identifier != window.customer.currentUserNumber) {
            // When a B2B customer gets their real customer number, update it everywhere
            await this._updateCurrentUserNumber(new_user_identifier);
        }

        localStorage.setItem('checkoutId', checkoutId);
        this.checkoutId = checkoutId;

        console.log(`📝 checkoutId >>`,checkoutId);

        // Dispatch checkout started event for shared cart service
        document.dispatchEvent(new CustomEvent('checkout:started', {
            detail: { checkoutId: this.checkoutId }
        }));

        // Handle AS400 response from start checkout
        console.log(`📝 AS/400 Response >>`,as400_response);
        if (as400_response) {
            console.log('🛒 Processing AS400 response from start checkout:', as400_response);

            // Check if order was auto-completed by backend
            if (auto_completed) {
                console.log('🎯 Order auto-completed by AS/400 approval:', message);
                return await this.finish(checkout);
            }

            if (Array.isArray(as400_response) && as400_response[0]?.status == 'J') {
                console.log('🎯 AS/400 status J detected - finishing order');
                return await this._approve();
            }

            if (Array.isArray(as400_response) && as400_response[0]?.status == 'N') {
                return this._handleStatusN();
            }

            // Process AS400 response for stock level changes
            // Note: This section only executes for non-J status responses
            // Status J responses are completed above and skip cart updates
            const addRequest = await this._updateLineStockChanges(as400_response);
            console.log(`📝 Update Line Stock Changes Response >>`,addRequest);

            if (addRequest?.length) {

                await this._update();

                document.dispatchEvent(new Event('timeout:start', { bubbles: true })); // start checkout timer 15 min timer;

                const cart = LocalCart.getState();
                document.querySelector('[data-cart-items]').innerText = JSON.stringify(cart.items);

                const issueItems = addRequest.filter(({ properties }) => Object.keys(properties).includes('_answer_type') && properties._resolved != true);

                console.log(`📝 Issue Items >>`,issueItems);

                if (issueItems.length == 0) {
                    return await this._approve();
                }

                await this.render();

                document.body.classList.remove('checkout-loading');
            } else {
                // No stock changes needed, proceed with approval
                console.log('🛒 No stock changes needed, proceeding with approval');
                return await this._approve();
            }
        } else {
        // Fallback to original data processing if no AS400 response
            if (Array.isArray(data) && data[0]?.status == 'J') {
                return await this.finish(checkout);
            }

            if (Array.isArray(data) && data[0]?.status == 'N') {
                return this._handleStatusN();
            }

            document.dispatchEvent(new Event('timeout:start', { bubbles: true })); // start checkout timer 15 min timer;

            const addRequest = await this._updateLineStockChanges(data);
            console.log(`📝 Update Line Stock Changes Response When No AS/400 Response >>`,addRequest);

            if (addRequest?.length) {
                await this._update();
                
                const cart = LocalCart.getState();
                document.querySelector('[data-cart-items]').innerText = JSON.stringify(cart.items);

                const issueItems = addRequest.filter(({ properties }) => Object.keys(properties).includes('_answer_type') && properties._resolved != true);
                console.log(`📝 Issue Items >>`,issueItems);

                if (issueItems.length == 0) {
                    return await this._approve();
                }

                await this.render();
                document.body.classList.remove('checkout-loading');
            } else {
                // No stock changes needed, proceed with approval
                console.log('🛒 No stock changes needed, proceeding with approval');
                return await this._approve();
            }
        }

        this.loading = false;

        return Promise.resolve();
    }

    async _sendStartCheckout() {

        const updateAt = new Date().getTime();
        localStorage.setItem('checkout_update_at', updateAt.toString());
        localStorage.setItem('checkout_started', true);

        await this.updateSubmitedBonus(); // update bonus balance attr

        // Use regular cart data
        const shopifyCart = LocalCart.getState();

        // Get cart items from the actual cart
        const allItems = shopifyCart.items;
        // Create payload from actual cart data
        const processedItems = this._itemsPayload(allItems);

        console.log(`allItems >>`,allItems)
        console.log(`processedItems >>`,processedItems)

        const cartPayload = {
            approved: false,
            deliveryDate: this._getCurrentDeliveryDate(),
            note: shopifyCart.note || '',
            attributes: shopifyCart.attributes || {},
            original_total_price: parseInt(shopifyCart.original_total_price || 0, 10),
            total_price: parseInt(shopifyCart.total_price || 0, 10),
            total_discount: parseInt(shopifyCart.total_discount || 0, 10),
            total_weight: parseInt(shopifyCart.total_weight || 0, 10),
            item_count: parseInt(shopifyCart.item_count || 0, 10),
            items: processedItems,
            requires_shipping: shopifyCart.requires_shipping,
            currency: shopifyCart.currency,
            items_subtotal_price: parseInt(shopifyCart.items_subtotal_price || 0, 10),
            cart_level_discount_applications: shopifyCart.cart_level_discount_applications || [],
            checkout_charge_amount: parseInt(shopifyCart.checkout_charge_amount || 0, 10)
        };

        const depositLines = await this._updateDeposit(cartPayload.items, allItems);
        console.log(`depositLines >>`,depositLines)
        if(depositLines && depositLines.length){
            cartPayload.items.push(...depositLines);
        }
        
        cartPayload.total_price = this._modifyTotalPrice(cartPayload.items);

        //get updated bonus amount and minus them from main total amonut
        const updatedBonusAmnt = cartPayload.attributes?.bonus_amount
        const bonusAmount = updatedBonusAmnt != null && updatedBonusAmnt !== '' ? parseInt(updatedBonusAmnt) * 100 : null;
        if(bonusAmount && bonusAmount > 0){
            cartPayload.total_price = parseInt(cartPayload.total_price - bonusAmount || 0, 10);
        }

        cartPayload.original_total_price = cartPayload.total_price;
        cartPayload.items_subtotal_price = cartPayload.total_price;
        cartPayload.item_count = cartPayload.items.length;

        // Send cart data for checkout creation - backend will generate checkoutId
        const requestPayload = {
            customerId: window.customer.id,
            userName: window.customer.userName,
            userNumber: window.customer.currentUserNumber,
            itemCount: cartPayload.items.length,
            complete: false,
            // Include full cart data for checkout creation
            checkout: {
                ...cartPayload,
                shipping_costs: await this._handleShippingCosts(),
            }
        };

        console.log(`Start checkout params >>>`, requestPayload);

        const checkoutStartResponse = await fetch('/apps/stroetmann/api/v1/checkouts/start_checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestPayload),
        });

        let startCheckoutResponsePayload;
        try {
            startCheckoutResponsePayload = await checkoutStartResponse.json();
        } catch {
            throw new ApiResponseError('Invalid server response (start_checkout)', { status: checkoutStartResponse.status });
        }

        console.log('🔍 START_CHECKOUT Response:', startCheckoutResponsePayload);
        console.log('🔍 START_CHECKOUT Status:', checkoutStartResponse.status);

        
        // Check for errors in the response
        if (!checkoutStartResponse.ok) {

            console.log('⚠️ START CHECKOUT HAD AN ERROR:', startCheckoutResponsePayload);

            // Handle timeout errors
            if (startCheckoutResponsePayload.timeout === true || startCheckoutResponsePayload.errors?.includes('server_offline')) {
                throw new OfflineError();
            }
            // Handle AS/400 errors
            if (startCheckoutResponsePayload.errors?.includes('socket_connection_error')) {
                throw new AS400ConnectionError('AS/400 server connection failed - please try again later');
            }
            if (startCheckoutResponsePayload.errors?.includes('server_request_timeout')) {
                throw new AS400TimeoutError('AS/400 request timed out - please try again');
            }

            //Fallback Generic error
            throw new ApiResponseError(startCheckoutResponsePayload.error || startCheckoutResponsePayload.message || 'Checkout start failed', {
                    status: checkoutStartResponse.status,
                    payload: startCheckoutResponsePayload
                }
            );
        }

        return startCheckoutResponsePayload;
    }

    /**
     * STOCK LEVEL CHECK functions
     */

    async handleStockLevelCheck() {
        try {
            this.loading = true;
            await this._stockLevelCheck();
        } catch (error) {

            //Single unified error handler
            await this._handleCheckoutError(error, 'handleStockLevelCheck');

            // // Handle other errors with user-friendly message
            // if (this._shouldShowErrorModal(e)) {
            //     this._showApiErrorModal(e, 'handleCreate');
            // } else {
            //     console.error('⚠️ Internal error >>', e);
            //     console.error('⚠️ The inventory check could not be performed. Please try again.');
            //     document.dispatchEvent(new CustomEvent('timeout:pause', { bubbles: true }));
            //     const modal = this.errorModal;
            //     modal.html = '';
            //     modal.stack = '';
            //     modal.message = 'The inventory check could not be performed. Please try again.';
            //     modal.open(this);
            //     this.loading = false;
            // }
        }
    }

    async _stockLevelCheck() {

        window.scrollTo({ top: 0, behavior: 'smooth' });

        const { response, errors } = await this._sendStockLevelCheck();

        if (Array.isArray(response) && response[0]?.status == 'J') {
            return this._approve();
        }

        if (Array.isArray(response) && response[0]?.status == 'N') {
            return this._handleStatusN();
        }

        const addRequest = await this._updateLineStockChanges(response);
        console.log(`📝 Update Line Stock Changes Response >>`,addRequest);

        if (addRequest?.length) {
            
            await this._update();

            const cart = LocalCart.getState();
            document.querySelector('[data-cart-items]').innerText = JSON.stringify(cart.items);

            const issueItems = addRequest.filter(({ properties }) => Object.keys(properties).includes('_answer_type') && properties._resolved != true);
            console.log(`📝 Issue Items >>`,issueItems);

            if (issueItems.length == 0) {
                return await this._approve();
            }

            await this.render();

        } else {
            // No stock changes needed, proceed with approval
            console.log('🛒 No stock changes needed, proceeding with approval');
            return await this._approve();
        }

        this.loading = false;

        return Promise.resolve();
    }

    async _sendStockLevelCheck() {

        const updateAt = new Date().getTime();
        localStorage.setItem('checkout_update_at', updateAt.toString());

        //const storedItems = JSON.parse(document.querySelector('[data-cart-items]').innerText);
        const cart = LocalCart.getState();
        const storedItems = cart.items;
        const alternatives = storedItems.filter(({ properties }) => Object.keys(properties).includes('_is_alternative_product') && properties._resolved != true);

        console.log(`alternatives items >>`,alternatives)

        const shopifyCart = LocalCart.getState();
        shopifyCart.items = alternatives;

        const hideItems = storedItems.filter(({ product_id, properties }) =>
            // Condition A: Check if this item's is NOT in the alternatives list
            !shopifyCart.items.some(({ properties }) => properties._identifier == product_id) &&
            // Condition B: Ensure we aren't already hiding this item
            !Object.keys(properties).includes('_hidden') &&
            // Condition C: Ensure this is a specific type of product (an 'answer' type)
            Object.keys(properties).includes('_answer_type')
        );

        console.log(`Hide items >>`,hideItems);

        await this._handleHideItems(hideItems);

        const shopifyCartFinal = LocalCart.getState();
        shopifyCart.total_price = this._modifyTotalPrice(shopifyCart.items);

        shopifyCart.original_total_price = shopifyCart.total_price;
        shopifyCart.items_subtotal_price = shopifyCart.total_price;

        const stockCheckRequest = await fetch('/apps/stroetmann/api/v1/checkouts/stock_level_check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                checkoutId: parseInt(this.checkoutId),
                customerId: window.customer.id,
                userName: window.customer.userName,
                userNumber: window.customer.currentUserNumber,
                checkout: {
                    ...shopifyCart,
                    original_total_price: parseInt(shopifyCart.original_total_price || 0, 10),
                    total_price: parseInt(shopifyCart.total_price || 0, 10),
                    total_discount: parseInt(shopifyCart.total_discount || 0, 10),
                    total_weight: parseInt(shopifyCart.total_weight || 0, 10),
                    item_count: parseInt(shopifyCart.items.length || 0, 10),
                    items_subtotal_price: parseInt(shopifyCart.items_subtotal_price || 0, 10),
                    checkout_charge_amount: parseInt(shopifyCart.checkout_charge_amount || 0, 10),
                    shipping_costs: await this._handleShippingCosts(),
                    updated_at: updateAt,
                    approved: false,
                }
            }),
        });

        let stockCheckResponse;
        try {
            stockCheckResponse = await stockCheckRequest.json();
        } catch {
            throw new ApiResponseError('Invalid server response (stock_level_check)', { status: stockCheckRequest.status });
        }
        
        // Check for errors in the response
        if (!stockCheckRequest.ok) {

            console.log('⚠️ STOCK CHECK HAD AN ERROR:', stockCheckResponse);

            // Handle timeout errors
            if (stockCheckResponse.timeout === true || stockCheckResponse.errors?.includes('server_offline')) {
                throw new OfflineError();
            }
            // Handle AS/400 errors
            if (stockCheckResponse.errors?.includes('socket_connection_error')) {
                throw new AS400ConnectionError('AS/400 server connection failed - please try again later');
            }
            if (stockCheckResponse.errors?.includes('server_request_timeout')) {
                throw new AS400TimeoutError('AS/400 request timed out - please try again');
            }

            //Fallback Generic error
            throw new ApiResponseError(stockCheckResponse.error || stockCheckResponse.message || 'Stock check failed', {
                    status: stockCheckRequest.status,
                    payload: stockCheckResponse
                }
            );
        }
        
        return stockCheckResponse;
    }

    /**
     * OFFLINE CONFIRMATION functions
     */
    async handleOfflineConfirmation() {
        try {
            this.loading = true;
            await this._offlineConfirmation();
            this.loading = false;
        } catch (error) {
            // Unified error handling
            await this._handleCheckoutError(error, 'handleOfflineConfirmation');
        }
    }

    async _offlineConfirmation() {

        return this._sendOfflineConfirmation();
    }

    async _sendOfflineConfirmation() {

        const stockCheckRequest = await fetch('/apps/stroetmann/api/v1/checkouts/offline_confirmation', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                checkoutId: parseInt(this.checkoutId),
                customerId: window.customer.id,
                userName: window.customer.userName,
                userNumber: window.customer.currentUserNumber,
            }),
        });

        let offlineConfirmResponsePayload;
        try {
            offlineConfirmResponsePayload = await stockCheckRequest.json();
        } catch {
            throw new ApiResponseError('Invalid server response (offline_confirmation)', { status: stockCheckRequest.status });
        }

        if (!stockCheckRequest.ok) {
            throw new ApiResponseError(offlineConfirmResponsePayload.error || offlineConfirmResponsePayload.message || 'Offline confirmation failed', {
                status: stockCheckRequest.status,
                payload: offlineConfirmResponsePayload
            });
        }

        return offlineConfirmResponsePayload;
    }

    /**
     * UPDATE functions
     */
    async handleUpdate(rerender = false, validate = true) {
        try {
            this.loading = true;
            if (validate) {
                await this._validate();
            }
            await this._update();
            rerender && await this.render();
            this.loading = false;
        } catch (error) {
            // Unified error handling
            await this._handleCheckoutError(error, 'handleUpdate');
        }
    }

    async _update() {
        return this._sendUpdate();
    }

    async _sendUpdate() {

        const updateAt = new Date().getTime();
        localStorage.setItem('checkout_update_at', updateAt.toString());

        await this.updateSubmitedBonus(); // update bonus balance attr

        // Use regular local cart data
        const shopifyCart = LocalCart.getState();

        // Get cart items from the actual cart
        const allItems = shopifyCart.items;

        // Create payload from actual cart data
        const processedItems = this._itemsPayload(allItems);

        console.log(`processedItems >>`,processedItems)

        const cartPayload = {
            approved: false,
            deliveryDate: this._getCurrentDeliveryDate(),
            note: shopifyCart.note || '',
            attributes: shopifyCart.attributes || {},
            original_total_price: parseInt(shopifyCart.original_total_price || 0, 10),
            total_price: parseInt(shopifyCart.total_price || 0, 10),
            total_discount: parseInt(shopifyCart.total_discount || 0, 10),
            total_weight: parseInt(shopifyCart.total_weight || 0, 10),
            item_count: parseInt(shopifyCart.item_count || 0, 10),
            items: processedItems,
            requires_shipping: shopifyCart.requires_shipping,
            currency: shopifyCart.currency,
            items_subtotal_price: parseInt(shopifyCart.items_subtotal_price || 0, 10),
            cart_level_discount_applications: shopifyCart.cart_level_discount_applications || [],
            checkout_charge_amount: parseInt(shopifyCart.checkout_charge_amount || 0, 10)
        };

        const depositLines = await this._updateDeposit(cartPayload.items, allItems);
        if(depositLines && depositLines.length){
            cartPayload.items.push(...depositLines);
        }

        cartPayload.total_price = this._modifyTotalPrice(cartPayload.items);

        //get updated bonus amount and minus them from main total amonut
        const updatedBonusAmnt = cartPayload.attributes?.bonus_amount
        const bonusAmount = updatedBonusAmnt != null && updatedBonusAmnt !== '' ? parseInt(updatedBonusAmnt) * 100 : null;
        if(bonusAmount && bonusAmount > 0){
            cartPayload.total_price = parseInt(cartPayload.total_price - bonusAmount || 0, 10);
        }

        cartPayload.original_total_price = cartPayload.total_price;
        cartPayload.items_subtotal_price = cartPayload.total_price;
        cartPayload.item_count = cartPayload.items.length;

        const requestPayload = {
            checkoutId: parseInt(this.checkoutId),
            customerId: window.customer.id,
            userName: window.customer.userName,
            userNumber: window.customer.currentUserNumber,
            itemCount: cartPayload.items.length,
            complete: false,
            // Include full cart data for checkout creation
            checkout: {
                ...cartPayload,
                shipping_costs: await this._handleShippingCosts(),
            }
        };

        const checkoutUpdateResponse = await fetch('/apps/stroetmann/api/v1/checkouts/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestPayload),
        });

        let updateResponsePayload;
        try {
            updateResponsePayload = await checkoutUpdateResponse.json();
        } catch {
            throw new ApiResponseError('Invalid server response (update)', { status: checkoutUpdateResponse.status });
        }

        console.log('🔍 UPDATE Response:', updateResponsePayload);
        
        // Check for errors in the response
        if (!checkoutUpdateResponse.ok) {

            console.log('⚠️ UPDATE CHECKOUT HAD AN ERROR:', updateResponsePayload);

            // Handle timeout errors
            if (updateResponsePayload.timeout === true || updateResponsePayload.errors?.includes('server_offline')) {
                throw new OfflineError();
            }
            // Handle AS/400 errors
            if (updateResponsePayload.errors?.includes('socket_connection_error')) {
                throw new AS400ConnectionError('AS/400 server connection failed - please try again later');
            }
            if (updateResponsePayload.errors?.includes('server_request_timeout')) {
                throw new AS400TimeoutError('AS/400 request timed out - please try again');
            }
            
            // Fallback Generic error
            throw new ApiResponseError(updateResponsePayload.error || updateResponsePayload.message || 'Checkout Update failed', {
                    status: checkoutUpdateResponse.status,
                    payload: updateResponsePayload
                }
            );

        }

        return updateResponsePayload;
    }

    /**
     * APPROVE functions
     */

    async handleApprove() {
        try {
            // Clear any previous error messages
            this._clearCartErrorMessage();
            
            // Check if there are any active items (not all canceled)
            const cart = LocalCart.getState();
            const cartItems = cart.items;
            const activeItems = cartItems.filter(item => {
                const isCanceled = item.properties?._canceled === true || item.properties?._canceled === 'true';
                const isDeposit = item.properties?._deposit === true || item.properties?._deposit === 'true';
                return !isCanceled && !isDeposit;
            });
            
            console.log(`📦 Approval check: ${activeItems.length} active items out of ${cartItems.length} total`);
            
            if (activeItems.length === 0 && cartItems.length > 0) {
                console.log('⚠️ All items are canceled/unavailable - cannot proceed');
                document.dispatchEvent(new CustomEvent('timeout:pause', { bubbles: true }));
                const modal = this.errorModal;
                modal.html = '';
                modal.stack = '';
                modal.message = 'Alle Artikel in Ihrem Warenkorb sind zum gewählten Liefertermin nicht verfügbar. Bitte wählen Sie andere Artikel oder einen anderen Liefertermin und versuchen Sie es erneut. Sollte das Problem weiterhin bestehen, kontaktieren Sie bitte unseren Kundenservice.';
                if(modal.querySelector(`.modal--heading`)) modal.querySelector(`.modal--heading`).textContent = `Oops!`;
                if(modal.querySelector(`.modal--general-info`)) modal.querySelector(`.modal--general-info`).textContent = ``;
                if(modal.querySelector(`.modal--svg-icon`)) modal.querySelector(`.modal--svg-icon`).remove();
                modal.open(this);
                this.loading = false;
                return;
            }

            // Dispatch checkout started event
            document.dispatchEvent(new CustomEvent('checkout:started', {
                detail: { type: 'approve', timestamp: Date.now() }
            }));

            this.loading = true;
            
            await this.updateSubmitedBonus(); // update bonus balance attr

            const checkout = await this._validate();

            //get updated bonus amount and minus them from main total amonut
            const updatedBonusAmnt = checkout.attributes?.bonus_amount
            const bonusAmount = updatedBonusAmnt != null && updatedBonusAmnt !== '' ? parseInt(updatedBonusAmnt) : null;
            if(bonusAmount && bonusAmount > 0){
                checkout.total_price = parseInt(checkout.total_price - bonusAmount || 0, 10);
            }

            checkout.original_total_price = checkout.total_price;
            checkout.items_subtotal_price = checkout.total_price;
            
            // Check if validation returned a stock update scenario
            if (checkout.items_unavailable || checkout.stock_issues) {
                console.log('📦 Stock issues detected, refreshing cart before approval...');
                
                // Wait for backend to process the stock update
                await this._sleep(2000);
                
                // Refresh the cart display
                await this.render();
                
                // Try validation again with updated cart
                const updatedCheckout = await this._validate();

                //get updated bonus amount and minus them from main total amonut
                const updatedBonusAmnt = updatedCheckout.attributes?.bonus_amount
                const bonusAmount = updatedBonusAmnt != null && updatedBonusAmnt !== '' ? parseInt(updatedBonusAmnt) : null;
                if(bonusAmount && bonusAmount > 0){
                    updatedCheckout.total_price = parseInt(updatedCheckout.total_price - bonusAmount || 0, 10);
                }

                updatedCheckout.original_total_price = updatedCheckout.total_price;
                updatedCheckout.items_subtotal_price = updatedCheckout.total_price;

                await this._approve(updatedCheckout);
            } else {
                await this._approve(checkout);
            }
        } catch (error) {
            // Unified error handling
            await this._handleCheckoutError(error, 'handleApprove');
        }
    }

    async _approve(checkout = null) {
        return await this._retryOperation((signal) => this._sendApprove(checkout, signal), 'approval');
    }

    /**
     * Retry operation with exponential backoff for backend API calls
     */
    async _retryOperation(operation, operationType = 'unknown', maxRetries = 2) {
        const baseDelay = 1000; // 1 second
        const maxDelay = this.retry_timeout * 1000; // Convert to milliseconds
        let lastError;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                console.log(`🛒 ${operationType} attempt ${attempt + 1}/${maxRetries}`);

                // Create AbortController for this attempt
                const controller = new AbortController();
                this.activeControllers.add(controller); // Track controller
                
                const timeoutId = setTimeout(() => {
                    console.log(`⏱️ ${operationType} timeout after ${this.api_timeout}s, aborting request...`);
                    controller.abort();
                }, this.api_timeout * 1000);

                try {
                    const result = await operation(controller.signal);
                    clearTimeout(timeoutId);
                    this.activeControllers.delete(controller); // Remove on success
                    return result;
                } catch (error) {
                    clearTimeout(timeoutId);
                    this.activeControllers.delete(controller); // Remove on error
                    
                    // if (error.name === 'AbortError') {
                    //     console.log(`🚫 ${operationType} request was aborted`);
                    //     throw new Error('Operation timeout');
                    // }
                    
                    if (error.name === 'AbortError') {
                        console.log(`🚫 ${operationType} request was aborted`);
                        throw new ApiResponseError('Request timed out', { status: 408 });
                    }

                    throw error;
                }
            } catch (error) {
                lastError = error;

                // Don't retry if error is not retryable
                if (error.retryable === false) {
                    console.log(`🛒 ${operationType} failed with non-retryable error:`, error.errorType);
                    throw error;
                }

                // Don't retry on last attempt
                if (attempt === maxRetries - 1) {
                    console.error(`🛒 ${operationType} failed after ${maxRetries} attempts`);
                    throw error;
                }

                // Calculate delay with exponential backoff, capped at maxDelay
                const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
                console.log(`🛒 ${operationType} failed, retrying in ${delay}ms. Error:`, error.message);

                // Add jitter to prevent thundering herd (up to 500ms)
                const jitteredDelay = delay + Math.random() * 500;
                await this._sleep(jitteredDelay);
            }
        }

        throw lastError;
    }

    /**
     * Sleep utility for retry delays
     */
    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    /**
     * Cleanup active requests to prevent orphaned operations
     */
    _cleanupActiveRequests() {
        if (this.activeControllers && this.activeControllers.size > 0) {
            console.log(`🧹 Cleaning up ${this.activeControllers.size} active request(s)`);
            this.activeControllers.forEach(controller => {
                try {
                    controller.abort();
                } catch (e) {
                    // Ignore errors during cleanup
                }
            });
            this.activeControllers.clear();
        }
    }

    async _sendApprove(validateCheckout = null, signal = null) {

        console.log(`In _sendApprove function >>`, validateCheckout,signal);

        document.dispatchEvent(new CustomEvent('timeout:pause', { bubbles: true }));

        await this._sendUpdate();

        const approvalPayload = {
            checkoutId: parseInt(this.checkoutId),
            customerId: window.customer.id,
            userName: window.customer.userName,
            userNumber: window.customer.currentUserNumber,
        };

        console.log('🔍 APPROVE Request Payload:', approvalPayload);

        window.scrollTo({ top: 0, behavior: 'smooth' });

        const approvalRequest = await fetch('/apps/stroetmann/api/v1/checkouts/approve', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(approvalPayload),
            signal: signal // Add abort signal for cancellation
        });

        // Read the response body once

        let approveResponsePayload;
        try {
            approveResponsePayload = await approvalRequest.json();
        } catch {
            throw new ApiResponseError('Invalid server response (approve)', { status: approvalRequest.status });
        }

        console.log('🔍 APPROVE Response:', approveResponsePayload);
        console.log('🔍 APPROVE Status:', approvalRequest.status);
        
        // Check for errors in response
        if (!approvalRequest.ok) {

            console.log('⚠️ APPROVE CHECKOUT HAD AN ERROR:', approveResponsePayload);

            if (approvalRequest.status == 404) {
                return this.finish(approvalPayload.checkout);
            }
            
            // Check if it's about unavailable items - this is handled specially
            if (approveResponsePayload.items_unavailable || approveResponsePayload.stock_issues) {
                console.log('📦 Items unavailable detected during approval - handling stock update...');
                
                // If there's a checkout object, continue with it
                if (approveResponsePayload.checkout) {
                    console.log('✅ Continuing with checkout despite unavailable items');
                    return this.finish(approveResponsePayload.checkout);
                }
                
                // If no checkout but has items_unavailable, handle as stock update needed
                if (approveResponsePayload.items_unavailable && Array.isArray(approveResponsePayload.items_unavailable)) {
                    console.log('📦 Processing unavailable items:', approveResponsePayload.items_unavailable);
                    // The backend should handle removing unavailable items
                    // We just need to refresh the cart after backend processing
                    
                    // Wait a moment for backend to process
                    await this._sleep(1000);
                    
                    // Refresh the cart state
                    await this.render();
                    
                    // Try approval again with updated cart
                    console.log('🔄 Retrying approval after stock update...');
                    return await this._sendApprove(null, signal);
                }
                
                // If we can't handle it, throw a more specific error
                throw new Error('Einige Artikel sind nicht verfügbar. Bitte überprüfen Sie Ihren Warenkorb.');
            } else if (approvalRequest.status >= 500) {
                throw new ApiResponseError(approveResponsePayload.error || approveResponsePayload.message || 'Server error during approval. Please try again.',{
                    status: approvalRequest.status,
                    payload: approveResponsePayload
                });
            } else if (approveResponsePayload.error || approveResponsePayload.message) {
                throw new ApiResponseError(approveResponsePayload.error || approveResponsePayload.message || 'Approval request failed',{
                    status: approvalRequest.status,
                    payload: approveResponsePayload
                });
            }
        }

        const { checkout } = approveResponsePayload;

        return this.finish(checkout);
    }

    /**
     * MODAL ERROR functions
     */

    async handleErrorModalReset() {
        this._errorModalButtonState(true);
        this.errorModalReset?.classList.add('is-loading');

        await this.clear(false);

        // reset checkout flag when is cancelled/reset
        console.log('🔓 Reseting checkout flag due to checkout reset...');
        LocalCart.updateCheckoutState();
        // Local cart update event so latest cart saved to metafield
        if(sharedCart && Object.keys(sharedCart).length){
            await sharedCart.handleCartChange();
        }

        const formData = new FormData();
        formData.append('attributes[locked]', '');
        formData.append('attributes[removed]', '');
        formData.append('attributes[bonus_amount]', '');
        formData.append('attributes[bonus_amount_max]', '');

        await this._updateCartAtributes(formData);

        // Dispatch checkout cancelled event
        document.dispatchEvent(new CustomEvent('checkout:cancelled', {
            detail: { reason: 'reset', timestamp: Date.now() }
        }));

        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 1 }));

        //await this.render();

        this.errorModal?.close(this);

        this._errorModalButtonState(false);
        this.errorModalReset?.classList.remove('is-loading');

        setTimeout(() => {
            window.location.reload();
        }, 100);
    }

    async handleErrorModalRestore() {
        this._errorModalButtonState(true);
        this.errorModalRestore?.classList.add('is-loading');

        // Unlock checkout flag when checkout is cancelled/restored
        console.log('🔓 Reseting checkout flag due to checkout restore...');
        LocalCart.updateCheckoutState();

        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 1 }));

        this._clearLocalStorage();

        const formData = new FormData();
        formData.append('attributes[locked]', '');
        formData.append('attributes[removed]', '');
        formData.append('attributes[bonus_amount]', '');
        formData.append('attributes[bonus_amount_max]', '');

        await this._updateCartAtributes(formData);
        await this._clearAlternateItemsFromCart();
        await this._clearItemProperties();
        //await this.render();

        document.body.classList.remove('checkout-loading');
        this.errorModal?.close(this);

        this._errorModalButtonState(false);
        this.errorModalRestore?.classList.remove('is-loading');

        setTimeout(() => {
            window.location.reload();
        }, 100);
    }

    _errorModalButtonState(disabled) {
        if (this.errorModalReset) {
            this.errorModalReset.disabled = disabled;
            this.errorModalReset.setAttribute('aria-disabled', `${disabled}`);
        }
        if (this.errorModalRestore) {
            this.errorModalRestore.disabled = disabled;
            this.errorModalRestore.setAttribute('aria-disabled', `${disabled}`);
        }
    }

    _shouldShowErrorModal(error) {
        return (error instanceof ApiResponseError || error?.isApiError === true || error?.isNetworkError === true || error instanceof AS400Error);
    }

    _showApiErrorModal(error, source = 'unknown') {
        console.log(`_showApiErrorModal >>`, error)
        document.dispatchEvent(new CustomEvent('timeout:pause', { bubbles: true }));

        this.errorModal.html = '';
        this.errorModal.stack = '';
        this.errorModal.message = error?.payload?.message || error?.message || 'An internal error has occurred. Please try again.';
        this.errorModal.open(this);
    }


    /**
     * MODAL OFFLINE CHECKOUT functions
     */

    async handleOfflineStockAccept() {

        this._handleOfflineStockButtonState(true);
        this.offlineStockAccept.classList.add('is-loading');

        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 3 }));

        await this.handleOfflineConfirmation();

        await this.finish(null);
    }

    async handleOfflineStockCancel() {

        this._handleOfflineStockButtonState(true);
        this.offlineStockCancel.classList.add('is-loading');

        // Unlock checkout flag when checkout is cancelled/restored
        console.log('🔓 Reseting checkout flag due to offline checkout cancellation...');
        LocalCart.updateCheckoutState();
        document.body.classList.remove('checkout-loading');

        // Dispatch checkout cancelled event
        document.dispatchEvent(new CustomEvent('checkout:cancelled', {
            detail: { reason: 'offline_stock_cancelled', timestamp: Date.now() }
        }));

        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 1 }));

        this._clearLocalStorage();
        await this._clearItemProperties();
        await this._clearAlternateItemsFromCart();
        await this.render();

        this.offlineStockModal?.close(this);

        this._handleOfflineStockButtonState(false);
        this.offlineStockCancel.classList.remove('is-loading');
    }

    _handleOfflineStockButtonState(disabled) {
        this.offlineStockCancel.disabled = disabled;
        this.offlineStockAccept.disabled = disabled;
        this.offlineStockCancel.setAttribute('aria-disabled', `${disabled}`);
        this.offlineStockAccept.setAttribute('aria-disabled', `${disabled}`);
    }

    /**
     * PUBLIC functions
     */

    async finish(payload) {

        const cartCheckout = document.querySelector('.cart__items');
        if (cartCheckout) {
            cartCheckout.style.opacity = .25;
        }

        this.loading = true;

        localStorage.setItem('checkout_approved', localStorage.getItem('checkout_timeout') || '');

        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 3 }));

        const cartJson = LocalCart.getState();

        const allItems = cartJson.items;

        cartJson.items = this._itemsPayload(cartJson.items);

        const depositLines = await this._updateDeposit(cartJson.items, allItems);
        if(depositLines && depositLines.length){
            cartJson.items.push(...depositLines);
        }

        localStorage.setItem('checkout', JSON.stringify({
            shipping: this.shipping,
            shipping_costs: await this._handleShippingCosts(),
            created_at: new Date().getTime(),
            checkout: payload,
            cart: cartJson,
            deliveryDate: cartJson.attributes.current_date,
            orderId: this.checkoutId ? `${this.checkoutId}` : null,
            shipping_text: `${this.dataset.shippingText}`,
            used_bonus_balance: cartJson.attributes?.bonus_amount ? Number(cartJson.attributes.bonus_amount) : 0
        }));

        // Clear cart approval attributes
        await this._clearApprovalState();
        await this.clear(false);
        
        // Set a flag to indicate order was just completed
        // This prevents draft order recreation from webhooks
        localStorage.setItem('order_just_completed', Date.now().toString());

        console.log('✅ Checkout completed → re synsc local cart metafield...');
        LocalCart.updateCheckoutState();
        if(sharedCart && Object.keys(sharedCart).length){
            await sharedCart.handleCartChange();
        }

        // Dispatch checkout completed event for shared cart service
        document.dispatchEvent(new CustomEvent('checkout:completed', {
            detail: { checkoutId: this.checkoutId }
        }));

        // CLEANUP: Remove ALL checkout protection before redirecting
        console.log('✅ Checkout completed successfully, removing all protection flags');
        document.body.dataset.checkoutActive = 'false';
        document.body.dataset.cartLocked = 'false';

        await this._sleep(750);
        
        setTimeout(() => {
          window.location.href = `/pages/${this.confirmationPage}`;    
        }, 350);
    }

    async render() {

        const mainSectionId = document.getElementById('cart-items')?.dataset.section || null;
        const mainCartSectionCSVId = document.querySelector('cart-csv')?.dataset.section || null;

        // empty section
        const sections = [];

        // Conditionally push sections if they exist
        if (mainSectionId) {
            sections.push(mainSectionId);
        }
        if (mainCartSectionCSVId) {
            sections.push(mainCartSectionCSVId);
        }

        if(!sections.length) return;

        const addResponseJson = await this._fetchJson(`/cart?sections=${sections.join(',')}&sections_url=${window.location.pathname}`);

        /* ---------- Main cart section ---------- */
        if (mainSectionId && addResponseJson[mainSectionId]) {
            const mainCartTemplate = document.createElement('template');
            mainCartTemplate.innerHTML = addResponseJson[mainSectionId];
            const mainCartEl = mainCartTemplate.content.querySelector(`#shopify-section-${mainSectionId}`);

            if (mainCartEl && document.querySelector('.cc-main-cart')) {
                document.querySelector('.cc-main-cart').innerHTML = mainCartEl.innerHTML;
            }
        }

        /* ---------- CSV import/export section ---------- */
        if (mainCartSectionCSVId && addResponseJson[mainCartSectionCSVId]) {
            const importExportTemplate = document.createElement('template');
            importExportTemplate.innerHTML = addResponseJson[mainCartSectionCSVId];
            const importExportEl = importExportTemplate.content.querySelector(`#shopify-section-${mainCartSectionCSVId}`);

            if (importExportEl && document.querySelector('.cc-csv-import')) {
                document.querySelector('.cc-csv-import').innerHTML = importExportEl.innerHTML;
            }
        }

        return;
    }

    async clear(bypass = true) {
        this._clearDeliveryDateShipping();
        this._clearLocalStorage();
        //await this._clearDiscounts();
        //await this._clearCartAtributes();
        //await this._cartClear(bypass);
        LocalCart.clearLocalCart();
    }

    /**
     * PRIVATE HELPER functions
     */

    async _handleHideItems(hideItems) {

        if (hideItems.length == 0) {
            return Promise.resolve(null);
        }

        hideItems.forEach(item => {
            const props = {
                ...item.properties,
                '_hidden': true
            };
            LocalCart.updateProps(item.id, props);
        });

        // Local cart update event so latest cart saved to metafield
        if(sharedCart && Object.keys(sharedCart).length){
            await sharedCart.handleCartChange();
        }
    }

    /**
     * Unified error handler for all checkout errors
     * Dispatches to specific handlers and ensures cleanup
     * @param {Error} error - The error to handle
     * @param {string} context - Where the error occurred (for logging)
     */
    async _handleCheckoutError(error, context = 'unknown') {
        console.error(`🚨 Checkout error in ${context}:`, error);
        
        // Map error types to handlers
        const errorHandlers = {
            'TimeoutError': () => this._handleTimeoutError(error),
            'AS400TimeoutError': () => this._handleAS400TimeoutError(error),
            'AS400ConnectionError': () => this._handleAS400ConnectionError(error),
            'AS400Error': () => this._handleAS400Error(error),
            'OfflineError': () => this._handleOfflineError(error),
            'ApiResponseError': () => this._handleApiResponseError(error),
        };
        
        // Get error type name
        const errorType = error.name || error.constructor.name;
        
        // Find and execute appropriate handler
        const handler = errorHandlers[errorType];
        
        if (handler) {
            handler();
        } else {
            // Generic error handling for unknown errors
            this._handleGenericError(error, context);
        }
        
        // ALWAYS cleanup on ANY error (prevents locked state)
        await this._cleanupOnCheckoutError();
        
        // Dispatch error event for monitoring/analytics
        document.dispatchEvent(new CustomEvent('checkout:error', {
            detail: { 
                type: errorType,
                context: context,
                message: error.message,
                timestamp: Date.now()
            }
        }));
    }

    /**
     * Cleanup operations that MUST run on every error
     * This prevents locked checkout states
     */
    async _cleanupOnCheckoutError() {
        console.log('🧹 Cleaning up checkout state after error');

        // Unlock checkout flag when checkout have an error
        console.log('🔓 Reseting checkout flag due to error...');
        LocalCart.updateCheckoutState();

        // CLEANUP: Remove ALL checkout protection flags (checkout-related localStorage)
        this._clearLocalStorage();
        //CLEANUP: all alternate items
        await this._clearAlternateItemsFromCart();
        //CLEANUP: all items extra props
        await this._clearItemProperties();

        // remove Lock attr for checkout
        console.log('🔒 Un-Locking checkout...');
        const formData = new FormData();
        formData.set('attributes[locked]', '');
        await this._updateCartAtributes(formData);
        
        // Update body data attributes
        document.body.dataset.checkoutActive = 'false';
        document.body.dataset.cartLocked = 'false';
        document.body.classList.remove('checkout-loading');
        
        // Reset to step 1
        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 1 }));
        
        // Reset loading state
        this.loading = false;
        
        if (this.submitCart) {
            this.submitCart.classList.remove('is-loading');
        }
    }

    /**
     * Handle generic/unknown errors
     */
    _handleGenericError(error, context) {
        console.error(`⚠️ Unhandled error type in ${context}:`, error);
        
        // Show user-friendly error
        if (this._shouldShowErrorModal(error)) {
            this._showApiErrorModal(error, context);
        } else {
            // this._showCartErrorMessage('An unexpected error occurred', 'Please try again or contact support if the problem persists');
            this._showCartErrorMessage('An unexpected error occurred', 'Please wait for 20 minutes if the order has not been placed automatically. If the problem persists, please contact support');
        }
    }

    /**
     * Handle OfflineError (when AS/400 is unavailable)
     */
    _handleOfflineError(error) {
        console.log('📡 System offline - showing offline modal');
        
        if (this.offlineStockModal) {
            this.offlineStockModal.message = error.message || 'The system is temporarily offline';
            this.offlineStockModal.open(this);
        } else {
            this._showCartErrorMessage('System Temporarily Offline', 'Please try again in a few moments');
        }
    }

    /**
     * Handle ApiResponseError
     */
    _handleApiResponseError(error) {
        console.error('🔌 API Error:', error.message, error.status);
        
        if (this._shouldShowErrorModal(error)) {
            this._showApiErrorModal(error, 'api_call');
        } else {
            this._showCartErrorMessage('Request Failed', error.message);
        }
    }

    _handleTimeoutError(error) {
        // Show timeout error modal with user-friendly message
        if (this.errorModal) {
            this.errorModal.html = '';
            this.errorModal.stack = '';
            this.errorModal.message = 'Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.';
            this.errorModal.open(this);
        }
    }

    _handleAS400TimeoutError(error) {
        console.error('🛒 AS/400 Timeout Error:', error.message);

        // Dispatch AS/400 timeout event
        document.dispatchEvent(new CustomEvent('checkout:as400:timeout', {
            detail: { error: error.message, timestamp: Date.now() }
        }));

        // Show user-friendly AS/400 timeout message
        this._showCartErrorMessage('AS/400 System Timeout', 'The order processing system is taking longer than expected. Please try again in a few minutes.');
    }

    _handleAS400ConnectionError(error) {
        console.error('🛒 AS/400 Connection Error:', error.message);

        // Dispatch AS/400 connection error event
        document.dispatchEvent(new CustomEvent('checkout:as400:connection_error', {
            detail: { error: error.message, timestamp: Date.now() }
        }));

        // Show user-friendly AS/400 connection message
        this._showCartErrorMessage('System Connection Issue', 'Unable to connect to the order processing system. Please check your internet connection and try again.');
    }

    _handleAS400Error(error) {
        console.error('🛒 AS/400 General Error:', error.message, error.errorType);

        // Dispatch AS/400 error event
        document.dispatchEvent(new CustomEvent('checkout:as400:error', {
            detail: {
                error: error.message,
                errorType: error.errorType,
                timestamp: Date.now()
            }
        }));

        // Show user-friendly AS/400 error message
        this._showCartErrorMessage('Order Processing Issue', 'There was an issue processing your order. Please try again or contact support if the problem persists.');
    }

    async _handleResponseError(response) {
        if (response.ok) {
            const clonedRequest = response.clone();

            try {
                let clonedRequestJSON = await clonedRequest.json();

                // Check for timeout indicator in successful response
                if (clonedRequestJSON.timeout === true) {
                    console.log('🛒 Response indicates timeout - triggering offline mode');
                    throw new OfflineError();
                }

                if (Object.keys(clonedRequestJSON).includes('errors')) {
                    const errorsContent = clonedRequestJSON['errors'].join('');

                    if (errorsContent.includes('server_offline')) {
                        throw new OfflineError();
                    }

                    // Handle AS/400 specific errors
                    if (errorsContent.includes('socket_connection_error')) {
                        throw new AS400ConnectionError('AS/400 server connection failed - please try again later');
                    }

                    if (errorsContent.includes('server_request_timeout')) {
                        throw new AS400TimeoutError('AS/400 request timed out - please try again');
                    }

                    if (errorsContent.includes('socket_connection_timeout')) {
                        throw new AS400ConnectionError('AS/400 connection timed out - server may be unavailable');
                    }

                    // Check for max_retries_exceeded which indicates persistent timeouts
                    if (errorsContent.includes('max_retries_exceeded')) {
                        console.log('🛒 AS/400 max retries exceeded - triggering offline mode');
                        throw new OfflineError();
                    }
                }
            } catch (parseError) {
                // Re-throw OfflineError if that's what was caught
                if (parseError instanceof OfflineError) {
                    throw parseError;
                }
                // Continue if JSON parsing fails on successful response
                console.warn('🛒 Unable to parse response JSON for error checking:', parseError.message);
            }

            return;
        }

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            let errorType = 'UNKNOWN_ERROR';
            let retryable = false;

            try {
                const errorData = await response.clone().json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
                if (errorData.description) {
                    errorMessage += ` - ${errorData.description}`;
                }
                if (errorData.error_type) {
                    errorType = errorData.error_type;
                }
            } catch (e) {
                // If we can't parse JSON, use the status text
                console.warn('🛒 Unable to parse error response JSON:', e.message);
            }

            // Categorize errors and determine if retryable
            switch (response.status) {
                case 400:
                    errorType = 'BAD_REQUEST';
                    retryable = false;
                    break;
                case 401:
                    errorType = 'UNAUTHORIZED';
                    retryable = false;
                    break;
                case 404:
                    errorType = 'NOT_FOUND';
                    retryable = false;
                    break;
                case 408:
                    errorType = 'TIMEOUT';
                    retryable = true;
                    // Check if this is an AS/400 timeout that should trigger offline mode
                    try {
                        const timeoutData = await response.clone().json();
                        if (timeoutData.timeout === true) {
                            console.log('🛒 Backend returned 408 timeout - triggering offline mode');
                            throw new OfflineError();
                        }
                    } catch (e) {
                        if (e instanceof OfflineError) {
                            throw e; // Re-throw OfflineError
                        }
                        // Continue with normal timeout handling if JSON parsing fails
                    }
                    break;
                case 409:
                    errorType = 'CONFLICT';
                    retryable = false;
                    break;
                case 429:
                    errorType = 'RATE_LIMITED';
                    retryable = true;
                    break;
                case 500:
                case 502:
                case 503:
                    errorType = 'SERVER_ERROR';
                    retryable = true;
                    break;
                case 504:
                    errorType = 'GATEWAY_TIMEOUT';
                    retryable = true;
                    // 504 Gateway Timeout should trigger offline mode like 408
                    try {
                        const timeoutData = await response.clone().json();
                        if (timeoutData.timeout === true) {
                            console.log('🛒 Backend returned 504 gateway timeout - triggering offline mode');
                            throw new OfflineError();
                        }
                    } catch (e) {
                        if (e instanceof OfflineError) {
                            throw e; // Re-throw OfflineError
                        }
                        // Continue with normal timeout handling if JSON parsing fails
                    }
                    // Also trigger offline mode for 504 without checking JSON payload
                    console.log('🛒 504 Gateway Timeout detected - triggering offline mode');
                    throw new Offlin_cleanupOnCheckoutErroreError();
                    break;
                default:
                    retryable = response.status >= 500;
            }

            const error = new Error(errorMessage);
            error.status = response.status;
            error.response = response;
            error.errorType = errorType;
            error.retryable = retryable;
            error.timestamp = Date.now();

            // Log error for debugging
            console.error(`🛒 Backend API Error [${errorType}]:`, {
                status: response.status,
                message: errorMessage,
                retryable,
                url: response.url
            });

            throw error;
        }
    }

    async _updateDeposit(items, allItems) {

        if (!Array.isArray(items) || items.length == 0) {
            return;
        }

        const depositPrices = JSON.parse(document.querySelector('[data-deposit-prices]').innerText);
        const depositProducts = JSON.parse(document.querySelector('[data-deposit-products]').innerText);

        const depositItems = items.filter((item) => item.properties['_deposit_reference']);

        const removeItems = allItems.filter((item) => item.properties['_deposit']);

        if (removeItems.length) {
            removeItems.forEach(item => LocalCart.updateQuantity(item.id, 0));
        }

        const depositLines = depositItems
            .map(({ properties, quantity }) => {
                const depositVariantId = parseInt(properties['_deposit_reference']);
                const deliveryDate = properties['_next_delivery_date'] || null;
                const totalDepositQuantity = depositItems
                    .filter((item) => item.properties['_deposit_reference'] == properties['_deposit_reference'])
                    .filter((item) => deliveryDate == item.properties['_next_delivery_date'])
                    .reduce((acc, cur) => acc + cur.quantity, 0);
                const depositPrice = depositPrices.find((element) => element.id == depositVariantId);
                return {
                    id: depositVariantId,
                    quantity: totalDepositQuantity,
                    price: depositPrice ? depositPrice.price : 0,
                    deliveryDate: deliveryDate
                };
            })
            .filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id && t.deliveryDate == obj.deliveryDate))
            .map(({ id, quantity, price, deliveryDate }) => {
                const properties = {
                    '_price': price,
                    '_deposit': true
                };
                if (deliveryDate) {
                    properties['_next_delivery_date'] = deliveryDate;
                }
                return {
                    id,
                    quantity,
                    properties: properties
                }
            });

        if (depositLines.length) {

            depositLines.forEach(item => {
                // We look for the first product where at least one variant matches the ID
                const depositItem = depositProducts.find(product =>  product.variants.some(variant => variant.id === item.id));
                const depositItemVariant = depositItem.variants.filter(variant => variant.id === item.id);
                const line_key = `${item.id}:${generateUniqueStringWithTimestamp()}`;
                let is_default_variant = true;
                if(depositItem.variants.length > 1) is_default_variant = false;

                LocalCart.addItem({
                    id: item.id,
                    key: line_key,
                    product_id: depositItem.id,
                    variant_id: item.id,
                    price: parseInt(item.properties._price),
                    compare_at_price: null,
                    line_price: (parseInt(item.properties._price) * item.quantity),
                    final_line_price: (parseInt(item.properties._price) * item.quantity),
                    discounted_price: parseInt(item.properties._price),
                    quantity: item.quantity,
                    handle: depositItem.handle,
                    title: depositItem.title,
                    sku: depositItemVariant[0]?.sku,
                    image: depositItem.featured_image,
                    properties: item.properties,
                    tags: depositItem.tags,
                    selected_variant: depositItemVariant[0]?.title,
                    options: depositItem.options,
                    vendor: depositItem.vendor,
                    product_has_only_default_variant: is_default_variant,
                    url: `/products/${depositItem.handle}?variant=${item.id}`,
                    variant_title: depositItemVariant[0]?.title,
                    line_level_discount_allocations: [],
                    line_level_total_discount: 0,
                    requires_shipping: true,
                    currency: Shopify?.currency?.active
                });
            });

            // Local cart update event so latest cart saved to metafield
            if(sharedCart && Object.keys(sharedCart).length){
                await sharedCart.handleCartChange();
            }

            const cart = LocalCart.getState();

            return cart.items.filter(({ properties }) => properties['_deposit'] == true); // return array containing all the deposit items;
        }

        return [];
    }

    async _updateLineStockChanges(updateLines) {

        // Handle null or undefined updateLines
        if (!updateLines || !Array.isArray(updateLines)) {
            console.log('🛒 No update lines to process, returning null');
            return Promise.resolve(null);
        }

        const cartItems = LocalCart.getState();

        const filteredUpdateLines = updateLines.filter(({ product_id }) => cartItems.items.some(({ properties }) => properties._identifier == product_id && properties._resolved != true));

        const storedItems = cartItems.items.map(({ id, quantity, properties, key }) => {
            return {
                id,
                quantity,
                properties,
                key,
            }
        });

        const addItems = await Promise.all(
            filteredUpdateLines
                .map(({ product_id, related_products, variant_shopify_ids, answer_type, comment, possible_next_delivery_date, available, order_id, remember_me, show_warning_msg }) => {
                    return new Promise(async (resolve) => {
                        const { quantity, properties, id } = storedItems.find(({ properties }) => properties._identifier == product_id);
                        const handles =
                            related_products.trim() == ''
                                ? []
                                : await Promise.all(
                                    variant_shopify_ids
                                        .split(',')
                                        .map((variantId => variantId.trim()))
                                        .filter((item) => item != '')
                                        .map((id) => new Promise(async (resolve) => {
                                            const handle = await this._fetchHandleByProductId(id);
                                            resolve(`${id}|${handle}`);
                                        }))
                                );
                        let nextDeliveryDateProperty = '';
                        if (possible_next_delivery_date && possible_next_delivery_date.trim() != '') {
                            const delivery_date_array = possible_next_delivery_date.split('');
                            const year = `${delivery_date_array[0]}${delivery_date_array[1]}${delivery_date_array[2]}${delivery_date_array[3]}`;
                            const month = `${delivery_date_array[4]}${delivery_date_array[5]}`;
                            const day = `${delivery_date_array[6]}${delivery_date_array[7]}`;
                            nextDeliveryDateProperty = `${day}${month}${year}`;
                        }
                        const lineItemUpdatePayload = {
                            id: id,
                            quantity: answer_type == 'S' ? quantity : quantity - parseInt(available),
                            properties: {
                                ...properties,
                                '_original_qty': quantity,
                                '_possible_next_delivery_date': nextDeliveryDateProperty,
                                '_answer_type': answer_type,
                                '_available': available,
                                '_comment': comment,
                                '_order_id': order_id,
                                '_product_id': product_id,
                                '_related_products': variant_shopify_ids,
                                '_remember_me': remember_me,
                                '_show_warning_msg': show_warning_msg,
                                '_variant_shopify_ids': variant_shopify_ids,
                                '_stroetmann_related_products': related_products,
                                '_related_information': handles.join('#'),
                                '_canceled': parseInt(available) == 0 && answer_type == 'E',
                                // '_resolved': quantity - parseInt(available) != quantity,
                            }
                        };
                        resolve(lineItemUpdatePayload);
                    })
                })
        )

        if (addItems.length == 0) {
            return Promise.resolve(null);
        }

        //update item properties for AS/400 unavailable items
        addItems.forEach(item => {
            LocalCart.updateProps(item.id, item.properties);
        });

        // Local cart update event so latest cart saved to metafield
        if(sharedCart && Object.keys(sharedCart).length){
            await sharedCart.handleCartChange();
        }

        return addItems;

    }

    async _fetchHandleByProductId(id) {
        const response = await fetch('/api/2021-07/graphql.json', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.token
            },
            body: JSON.stringify({
                "query": `{
                        products(query:"${id}", first:1) {
                            nodes {
                                handle
                            } 
                        }
                    }`
            })
        });
        const responseJson = await response.json();
        return responseJson.data?.products?.nodes[0].handle || null;
    }

    async _updateCartAtributes(formData) {
        return fetch(`${window.theme.routes.cartUpdate}.js`, {
            method: 'POST',
            headers: {
                'Bypass-Internal': 'true',
                'No-Queue': 'true'
            },
            body: formData,
        });
    }

    async _updateCurrentUserNumber(current_user_number) {

        window.customer.currentUserNumber = current_user_number;

        document.querySelector('[data-header-user-number]').innerHTML = current_user_number;
        document.querySelector('[data-header-user-number-text]').innerHTML = '';

        const formData = new FormData();
        formData.append('attributes[current_user]', current_user_number);
        await this._updateCartAtributes(formData);

        return fetch('/apps/stroetmann/api/v1/customers/set_current_user', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                customerId: window.customer.id,
                userName: window.customer.userName,
                userNumber: current_user_number
            })
        });
    }

    async _fetchJson(url) {
        const request = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'No-Queue': 'true'
            }
        });
        return request.json();
    }

    async _handleStatusN() {

        // Notify backend that checkout should be cancelled due to AS400 status N
        if (this.checkoutId) {
            try {
                console.log('🔓 Notifying backend to cancel checkout due to AS400 status N');
                
                const cancelResponse = await fetch('/apps/stroetmann/api/v1/checkouts/cancel', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        checkoutId: parseInt(this.checkoutId),
                        reason: 'as400_status_n',
                        timestamp: Date.now()
                    })
                });

                if (cancelResponse.ok) {
                    console.log('✅ Backend checkout cancelled successfully');
                } else {
                    console.warn('⚠️ Backend checkout cancellation failed:', cancelResponse.status);
                }
            } catch (error) {
                console.error('❌ Failed to notify backend of checkout cancellation:', error);
            }
        }

        // CLEANUP: Remove ALL checkout protection flags
        console.log('🔓 AS400 rejected order - removing all protection flags');
        this._clearLocalStorage();
        await this._clearItemProperties();
        await this._clearAlternateItemsFromCart();

        document.body.dataset.checkoutActive = 'false';
        document.body.dataset.cartLocked = 'false';

        // Clear the checkout started flag
        console.log('🔓 Releasing checkout started flag due to AS400 status N');
        LocalCart.updateCheckoutState();
        if(sharedCart && Object.keys(sharedCart).length){
            await sharedCart.handleCartChange();
        }

        // Clear cart approval attributes
        await this._clearApprovalState();

        // Dispatch checkout cancelled event
        document.dispatchEvent(new CustomEvent('checkout:cancelled', {
            detail: { reason: 'as400_status_n', timestamp: Date.now() }
        }));

        document.dispatchEvent(new CustomEvent('checkout:steps:to', { bubbles: true, detail: 1 }));
        
        document.body.classList.remove('checkout-loading');

        // Show error message in cart instead of modal
        this._showCartErrorMessage(window.settings.i18n.errors.as400_status_n, 'AS/400 Response Status: N');
        
        // Reset checkout component state
        this.loading = false;
        this.checkoutId = null;

        await this.render();
    }

    _showCartErrorMessage(message, subtext = null) {
        const cartErrorsElement = document.getElementById('cart-errors');
        if (cartErrorsElement) {
            // Clear existing content
            cartErrorsElement.innerHTML = '';

            // Create error content container
            const contentDiv = document.createElement('div');
            contentDiv.className = 'flex items-start justify-between';

            // Create message container
            const messageDiv = document.createElement('div');
            messageDiv.className = 'flex-1';

            // Add subtext first if provided
            if (subtext) {
                const subtextDiv = document.createElement('div');
                subtextDiv.className = 'text-sm mb-1 opacity-75 font-bold';
                subtextDiv.textContent = subtext;
                messageDiv.appendChild(subtextDiv);
            }

            // Main error message
            const mainMessage = document.createElement('div');
            mainMessage.className = 'font-medium';
            mainMessage.textContent = message;
            messageDiv.appendChild(mainMessage);

            // Create close button with better styling
            const closeButton = document.createElement('button');
            closeButton.className = 'ml-4 flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 group';
            closeButton.setAttribute('aria-label', window.theme?.strings?.close || 'Schließen');
            closeButton.innerHTML = `
                <svg class="w-4 h-4 text-red-600 group-hover:text-red-700 transition-colors duration-200" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            `;

            // Add close functionality
            closeButton.addEventListener('click', () => {
                this._clearCartErrorMessage();
            });

            // Assemble the error message
            contentDiv.appendChild(messageDiv);
            contentDiv.appendChild(closeButton);
            cartErrorsElement.appendChild(contentDiv);

            // Show the error
            cartErrorsElement.hidden = false;
            
            // Scroll to the error message
            cartErrorsElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
            });
            
            console.log('📢 Cart error message displayed:', message, subtext ? `(${subtext})` : '');
        } else {
            console.warn('⚠️ Cart errors element not found, falling back to console message');
            console.error('AS400 Error:', message, subtext ? `(${subtext})` : '');
        }
    }

    _clearCartErrorMessage() {
        const cartErrorsElement = document.getElementById('cart-errors');
        if (cartErrorsElement) {
            cartErrorsElement.hidden = true;
            cartErrorsElement.textContent = '';
        }
    }

    async _clearApprovalState() {
        console.log('🧹 Clearing all approval state indicators');

        try {
            // Clear cart attributes that trigger approval mode using JSON
            const updateData = {
                attributes: {
                    locked: null,
                    bonus_amount: null,
                    bonus_amount_max: null,
                    draft_order_locked: null,
                    draft_order_locked_at: null,
                    draft_order_lock_reason: null
                }
            };

            console.log('🔧 Clearing cart attributes:', Object.keys(updateData.attributes));
            
            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Bypass-Internal': 'true',
                    'No-Queue': 'true'
                },
                body: JSON.stringify(updateData)
            });
            
            if (response.ok) {
                const cartData = await response.json();
                console.log('✅ Cart attributes cleared successfully');
                console.log('🔍 Remaining cart attributes:', cartData.attributes);
            } else {
                console.error('❌ Failed to clear cart attributes:', response.status);
            }

            // Update DOM body attributes to reflect normal cart state
            const body = document.querySelector('body');
            if (body) {
                body.dataset.cartLocked = 'false';
                body.dataset.checkoutPresent = 'false';
                console.log('🔄 Updated body data attributes');
            }

            // Remove cart-locked class from cart container
            const cartContainer = document.getElementById('cart__container');
            if (cartContainer) {
                cartContainer.classList.remove('cart-locked');
                console.log('🎨 Removed cart-locked CSS class');
            }

            // Clear checkout data element if it exists
            const checkoutDataElement = document.querySelector('[data-checkout]');
            if (checkoutDataElement) {
                checkoutDataElement.innerHTML = '{"checkout":{"approved":true,"updated_at":0}}';
                console.log('📄 Reset checkout data element');
            }

            // // Trigger cart re-render to update UI
            // if (window.render) {
            //     await window.render();
            //     console.log('🔄 Cart UI refreshed');
            // }

            console.log('✅ Approval state cleared successfully');

        } catch (error) {
            console.error('❌ Failed to clear approval state:', error);
        }
    }

    _payloadHeader(payload) {
        return payload;
        // return {
        //     deliveryDate: payload?.deliveryDate,
        //     note: payload?.note,
        //     attributes: payload?.attributes,
        //     original_total_price: payload?.original_total_price,
        //     total_price: payload?.total_price,
        //     total_discount: payload?.total_discount,
        //     items: payload?.items ?? [],
        //     currency: payload?.currency,
        //     items_subtotal_price: payload?.items_subtotal_price,
        //     cart_level_discount_application: payload?.cart_level_discount_application,
        //     cart_level_discount_applications: payload?.cart_level_discount_applications,
        // }
    }

    _restoreItems(items) {
        const updateItems = this._itemsPayload(items);
        return items
            .filter(({ properties }) => properties['_deposit'] != true)
            .filter(({ properties }) => !updateItems.some((updateItem) => JSON.stringify(updateItem.properties) == JSON.stringify(properties)))
            .map((item) => [`${item.id}:${item.quantity}`, item.properties])
    }

    _itemsPayload(items) {
        items = this._filterCheckoutItems(items);
        items = this._modifyForBackend(items);
        items = this._transformReduce(items);
        return items;
    }

    _transformReduce(items) {
        return items.map(({
            id,
            handle,
            sku,
            properties,
            quantity,
            variant_id,
            product_id,
            key,
            title,
            price,
            line_price,
            discounted_price,
            original_line_price,
            final_line_price,
            discounts,
            line_level_discount_allocations,
            line_level_total_discount,
            total_discount,
            vendor,
            product_has_only_default_variant,
            variant_title,
            url,
            requires_shipping,
            currency
        }) => ({
            id,
            handle,
            sku,
            properties,
            quantity: parseInt(quantity || 0, 10),
            variant_id,
            product_id,
            key,
            title,
            price: parseInt(price || 0, 10),
            line_price: parseInt(line_price || 0, 10),
            discounted_price: parseInt(discounted_price || 0, 10),
            original_line_price: parseInt(original_line_price || 0, 10),
            final_line_price: parseInt(final_line_price || 0, 10),
            discounts,            
            line_level_discount_allocations,
            line_level_total_discount: parseInt(line_level_total_discount || 0, 10),
            total_discount: parseInt(total_discount || 0, 10),
            vendor,
            product_has_only_default_variant,
            variant_title,
            url,
            requires_shipping,
            currency
        }));
    }

    _modifyForBackend(items) {
        return items.map((item) => {
            if (!Object.keys(item.properties).includes('_resolved') && Object.keys(item.properties).includes('_answer_type') && ['E', 'S'].includes(item.properties['_answer_type'])) {
                const line_price = parseInt(item.properties['_answer_type'] == 'S' ? item.price * parseInt(item.quantity) : item.price * parseInt(item.properties['_available']), 10);
                const qty = item.properties['_answer_type'] == 'S' ? item.quantity : parseInt(item.properties['_available']);
                return {
                    ...item,
                    line_price: line_price,
                    quantity: qty,
                    price: parseInt(item.price, 10),
                    final_line_price: line_price,
                    original_line_price: parseInt(item.price) * parseInt(qty),
                    discounted_price: parseInt(item.price, 10),
                }
            } else if (Object.keys(item.properties).includes('_deposit')) {
                return {
                    ...item,
                    price: Math.round(item.price / 100.0),
                    line_price: Math.round(item.price / 100.0 * parseInt(item.quantity)),
                }
            }
            return item;
        })
        .filter((item) => item.quantity > 0);
    }

    _modifyTotalPrice(items) {
        return items.reduce((acc, cur) => acc + parseInt(cur.final_line_price || 0, 10), 0);
    }

    _filterCheckoutItems(items) {

        return items.filter(({ id, quantity, properties }) => {

            if (Object.keys(properties).includes('_canceled') && properties['_canceled'] == true) {
                return false;
            }

            if (Object.keys(properties).includes('_deposit') && properties['_deposit'] == true) {
                return false;
            }

            if (!Object.keys(properties).includes('_possible_next_delivery_date')) {
                return true;
            }

            if (Object.keys(properties).includes('_answer_type') && ['I', 'S'].includes(properties['_answer_type'])) {
                return true;
            }

            const hasAlternativeDeliveryProduct = items
                .filter(() => !Object.keys(properties).includes('_resolved'))
                .filter((currentItem) => currentItem.id == id)
                .filter((currentItem) => Object.keys(currentItem.properties).includes('_resolved'))
                .filter((currentItem) => Object.keys(currentItem.properties).includes('_next_delivery_date'))
                .filter((currentItem) => currentItem.properties['_next_delivery_date'] == properties['_possible_next_delivery_date'])
                .filter((currentItem) => quantity == currentItem.quantity)
                .length > 0;

            if (hasAlternativeDeliveryProduct) {
                return false;
            }

            return true;
        });
    }

    _filterDummy(items) {
        return items.filter(({ product_id }) => window.settings.dummy.product_id != product_id);
    }

    _getCurrentDeliveryDate() {
        if (this.deliveryDate) {
            const day = this.deliveryDate.getDate() < 10 ? `0${this.deliveryDate.getDate()}` : this.deliveryDate.getDate();
            const month = this.deliveryDate.getMonth() + 1 < 10 ? `0${this.deliveryDate.getMonth() + 1}` : this.deliveryDate.getMonth() + 1;
            const year = this.deliveryDate.getFullYear();
            return `${day}${month}${year}`;
        }
        return '';
    }

    async _validate() {
        return this._fetchJson('/cart?view=draft-order'); 
    }

    async _handleShippingCosts(useLocalStorage = false) {
        const storageKey = 'checkout_shipping';
        if (this.empty || !this.deliveryDate) {
            !this.deliveryDate && document.dispatchEvent(new Event("main-calendar:on:open", { bubbles: true }));
            return;
        }
        if (this.isCartPage) {
            document.querySelector('[data-summary-shipping-skeleton]')?.classList.remove('hidden-i');
            document.querySelector('[data-summary-shipping]')?.classList.add('hidden');
        }
        const items =
            this._filterDummy(
                JSON.parse(document.querySelector('[data-cart-items]').innerText)
                    .filter(({ properties }) => !Object.keys(properties).includes('_deposit')));
        const different_delivery_dates = Array.from(
            new Set(
                items
                    .filter(({ properties }) => Object.keys(properties).includes('_next_delivery_date') && properties._next_delivery_date != null)
                    .map(({ properties }) => properties._next_delivery_date)
                    .flat()
            )
        )
        const total_for_initial_delivery = items
            .filter(({ properties }) => !Object.keys(properties).includes('_next_delivery_date'))
            .reduce((acc, cur) => acc + parseInt(cur.final_line_price || 0, 10), 0);
        const mainShippingCosts = {
            'delivery_date': null,
            'amount': 0
        };
        const deliveryDateShippingCosts = different_delivery_dates.map((delivery_date) => {
            const total_for_delivery_date = items
                .filter(({ properties }) => Object.keys(properties).includes('_next_delivery_date') && properties._next_delivery_date == delivery_date)
                .reduce((acc, cur) => acc + parseInt(cur.final_line_price || 0, 10), 0);
            return {
                'delivery_date': delivery_date,
                'amount': total_for_delivery_date > this.shippingFreeThreshold ? 0 : this.defaultShippingCosts
            }
        });
        const totalDeliveries = [mainShippingCosts, ...deliveryDateShippingCosts];
        const deliveryDatesChanged = this._deliveryDatesChanged(totalDeliveries, storageKey);
        const shippingCosts = useLocalStorage || !deliveryDatesChanged ? JSON.parse(localStorage.getItem(storageKey)) : await Promise.all(totalDeliveries.map(({ delivery_date }) => {
            return new Promise(async (resolve) => {
                const currentDeliveryDate = delivery_date ? delivery_date : `${this.deliveryDate.getDate() < 10 ? `0${this.deliveryDate.getDate()}` : this.deliveryDate.getDate()}${this.deliveryDate.getMonth() + 1 < 10 ? `0${this.deliveryDate.getMonth() + 1}` : this.deliveryDate.getMonth() + 1}${this.deliveryDate.getFullYear()}`;
                const total_for_delivery_date = delivery_date == null ? total_for_initial_delivery : items
                    .filter(({ properties }) => Object.keys(properties).includes('_next_delivery_date') && properties._next_delivery_date == delivery_date)
                    .reduce((acc, cur) => acc + parseInt(cur.final_line_price || 0, 10), 0);
                // Note: B2B customers with temporary numbers should still get shipping/bonus calculations
                // The backend will handle them appropriately even with b2b_ prefix
                // Removed the early return that was preventing proper cost calculations
                const openOrderRequest = await fetch('/apps/stroetmann/api/v1/users/bonus_day', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        customerId: window.customer.id,
                        userName: window.customer.userName,
                        userNumber: window.customer.currentUserNumber,
                        deliveryDate: currentDeliveryDate
                    })
                });
                let orderShippingValue = 0;
                let orderBonusValue = 0;
                if (openOrderRequest.ok) {
                    const { bonus_day } = await openOrderRequest.json();
                    //const { order_shipping_costs_value, order_scale_determination_value } = bonus_day[0];
                    const { order_shipping_costs_value = 0, order_scale_determination_value = 0 } = bonus_day[0] || {};
                    orderShippingValue = order_shipping_costs_value;
                    orderBonusValue = order_scale_determination_value;
                }
                resolve({
                    delivery_date: delivery_date,
                    cart_total: total_for_delivery_date,
                    open_day_total: orderShippingValue,
                    day_total: parseInt(total_for_delivery_date) + parseInt(orderShippingValue),
                    amount: (parseInt(total_for_delivery_date) + parseInt(orderShippingValue)) > this.shippingFreeThreshold ? 0 : this.defaultShippingCosts,
                    bonus: orderBonusValue,
                });
            })
        }));
        const { amount } = shippingCosts.find(({ delivery_date }) => delivery_date == null);
        if (this.isCartPage && window.formatMoney) {
            document.querySelector('[data-summary-shipping]').innerHTML = window.formatMoney(amount);
            document.querySelector('[data-summary-shipping-skeleton]').classList.add('hidden-i');
            document.querySelector('[data-summary-shipping]').classList.remove('hidden');
        }
        localStorage.setItem(storageKey, JSON.stringify(shippingCosts));
        return shippingCosts;
    }

    _deliveryDatesChanged(totalDeliveries, storageKey) {
        try {
            const storedDeliveries = JSON.parse(localStorage.getItem(storageKey));
            const storedDates = storedDeliveries?.map(({ delivery_date }) => delivery_date ? delivery_date : '0')?.sort()?.join();
            const requestedDates = totalDeliveries?.map(({ delivery_date }) => delivery_date ? delivery_date : '0')?.sort()?.join();
            return storedDates != requestedDates;
        } catch (e) {
            console.log(e);
        }
        return true;
    }

    async updateSubmitedBonus(){

        const bonusInputEle = this.querySelector(`discount-input input[data-bonus-amount]`);
        if((bonusInputEle?.value?.trim() === '' || bonusInputEle?.value?.trim() === '0' || bonusInputEle?.value?.trim() === '0.0') || !window.bonusAmountValue){
            return;
        }

        const cart = LocalCart.getState();
        if(!cart.items.length) return;

        // Create payload from actual cart data
        const processedItems = this._itemsPayload(cart.items);
        console.log(`processedItems >>`, processedItems);
        if(!processedItems.length) return;

        let total = 0;
        processedItems.forEach(item => {
            if(!item.properties['_deposit']){
                total += item.final_line_price;
            }
        });

        let finalTotal = total/100;

        const currentBonusAmount = cart.attributes?.bonus_amount;
        //const currentBonusAmountMax = cart.attributes?.bonus_amount_max;
        const bonusAmount = currentBonusAmount != null && currentBonusAmount !== '' ? Number(currentBonusAmount) : null;
        window.bonusAmountValue = bonusAmount ? Number(bonusAmount) : 0;

        console.log(`currentBonusAmount >>`, bonusAmount, `finalTotal >>`,finalTotal);

        //update cart attributes for bonus amount. if inputed bonus amount is greater than cart total
        if(bonusAmount && bonusAmount > finalTotal){
            LocalCart.updateAttr({"bonus_amount": finalTotal.toFixed(2)});
            if(bonusInputEle) bonusInputEle.value = finalTotal.toFixed(2);

            if(sharedCart && Object.keys(sharedCart).length){
                await sharedCart.handleCartChange();
            }
        }
    }

    async _safeFetchJson(promise) {
        const res = await promise;
        if (res && typeof res.json === 'function') {
            return await res.json();
        }
        return res;
    }


    /**
     * PRIVATE CLEAR HELPER functions
     */

    _clearDeliveryDateShipping() {

        document.dispatchEvent(new CustomEvent('main-calendar:date:clear', { bubbles: true }));

        this.deliveryDate && localStorage.removeItem(`${window.customer.currentUserNumber}_${this.deliveryDate.getDate() < 10 ? `0${this.deliveryDate.getDate()}` : this.deliveryDate.getDate()}${this.deliveryDate.getMonth() + 1 < 10 ? `0${this.deliveryDate.getMonth() + 1}` : this.deliveryDate.getMonth() + 1}${this.deliveryDate.getFullYear()}_bds`);

        const items =
            this._filterDummy(
                JSON.parse(document.querySelector('[data-cart-items]').innerText)
                    .filter(({ properties }) => !Object.keys(properties).includes('_deposit')));

        Array.from(
            new Set(
                items
                    .filter(({ properties }) => Object.keys(properties).includes('_next_delivery_date') && properties._next_delivery_date != null)
                    .map(({ properties }) => properties._next_delivery_date)
                    .flat()
            )
        ).forEach((date) => {
            localStorage.removeItem(`${window.customer.currentUserNumber}_${date}_bds`);
            localStorage.removeItem(`${window.customer.currentUserNumber}_${date}_bonus`);
        });
    }

    _clearLocalStorage() {
        localStorage.removeItem(`${window.customer.currentUserNumber}_bonus_response`);
        localStorage.removeItem(`${window.customer.currentUserNumber}_bonus_balance`);
        localStorage.removeItem('related_products');
        localStorage.removeItem('checkout_timeout');
        localStorage.removeItem('checkout_update_checkout_timeout');
        localStorage.removeItem('checkout_progress');
        localStorage.removeItem('checkout_approved');
        localStorage.removeItem('checkoutId');
        localStorage.removeItem('checkout_update_at');
        localStorage.removeItem('checkout_protection_active');
        localStorage.removeItem('checkout_started');
        localStorage.removeItem('checkout_lock_timestamp');
        localStorage.removeItem('checkout_shipping');
    }

    async _clearDiscounts() {

        const cart = await this._fetchJson(`${window.theme.routes.cart}.js`);

        const query = `mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
                cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                    cart {
                        id
                        discountCodes {
                            code
                        }
                    }
                    userErrors {
                        field
                        message
                    }
                    warnings {
                        code
                        message
                    }
                }
            }`;

        const variables = {
            "cartId": `gid://shopify/Cart/${cart.token}`,
            "discountCodes": []
        };

        return fetch('/api/2024-10/graphql.json', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.token
            },
            body: JSON.stringify({ query: query, variables: variables })
        });
    }

    async _clearCartAtributes() {
        const formData = new FormData();
        formData.set('attributes[current_date]', '');
        formData.set('attributes[checkout_timeout]', '');
        formData.set('attributes[locked]', '');
        formData.set('attributes[removed]', '');
        formData.set('attributes[bonus_amount]', '');
        formData.set('attributes[bonus_amount_max]', '');
        formData.set('attributes[delivery_time_from]', '');
        formData.set('attributes[delivery_time_to]', '');

        return this._updateCartAtributes(formData);
    }

    async _cartClear(bypass = true) {

        if (bypass) {
            return fetch('/cart/clear.js', {
                headers: {
                    'Bypass-Internal': 'true',
                },
            });
        } else {
            return fetch('/cart/clear.js');
        }

    }

    async _clearAlternateItemsFromCart() {

        const { items } = LocalCart.getState();
        if(!items.length) return;
        let isAlternateRemoved = false;

        for (let i=0; i<items.length; i++){
            if(items[i].properties['_is_alternative_product'] || items[i].properties['_is_alternative_product'] == 'true'){
                LocalCart.updateQuantity(items[i].id, 0);
                isAlternateRemoved = true;
            }
        }

        // Local cart update event so latest cart saved to metafield
        if(isAlternateRemoved){
            if(sharedCart && Object.keys(sharedCart).length){
                await sharedCart.handleCartChange();
            }
        }
    }

    async _clearItemProperties() {

        const { items } = LocalCart.getState();
        if(!items.length) return;
        
        for (let i=0; i<items.length; i++){
            const properties = {
                '_price': items[i].properties['_price'],
                '_identifier': items[i].properties['_identifier'],
                ...(items[i].properties['_deposit_reference'] ? { '_deposit_reference': items[i].properties['_deposit_reference'] } : {}),
                ...(items[i].properties['_deposit'] ? { '_deposit': items[i].properties['_deposit'] } : {})
            }
            LocalCart.updateProps(items[i].id, properties);
        }
        
        // Local cart update event so latest cart saved to metafield
        if(sharedCart && Object.keys(sharedCart).length){
            await sharedCart.handleCartChange();
        }
    }
}

if (!customElements.get('cart-checkout')) {
    customElements.define('cart-checkout', CartCheckout);
}

if (!window.location.pathname.includes(`/cart`)) {
    window.checkout = new CartCheckout();
}